// Context
import { useEffect, useState } from "react";
import { useChats } from "../../../hooks/useChats";
import Modal from "../../common/Modal";
import Loader from "../../common/Loader";
import api from "../../../services/api";
import Switch from "react-switch";
import { useRipple } from "../../../hooks/useRipple";
import { convertHTMLToText, normalizList, planOfCareToArray, sectionToHTML } from "../../../utils/utils";
import { diffChars } from "diff";
import { useTranslation } from "react-i18next";
import { sanitizeStandardSectionHtml } from "../../../utils/sanitizeHtml";
import { parseSupportingDocs } from "../../../utils/icd10SupportingDocs";

function ModalIC10OptimizerNote({ close, restoreScroll, initialIcd10 }) {
  const { t } = useTranslation();
  const createRipple = useRipple();
  const [icd10, setIcd10] = useState([]);

  const { chat, updateEncounter } = useChats();
  const [loading, setLoading] = useState(false);
  const [generateFailed, setGenerateFailed] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [optimizations, setOptimizations] = useState();

  const [hpi, setHpi] = useState();
  const [plan, setPlan] = useState();
  const [planHTML, setPlanHTML] = useState();
  const [hpiHTML, setHpiHTML] = useState();

  // Look up the noteDesign section for a given context-variable name so the
  // HPI/Plan-of-Care addendum previews below route through the strict
  // sanitizer when that section carries user-authored output instructions (or
  // is itself a custom section) — same threat as any other note-section
  // render: a coerced <img src>/style url() would exfiltrate PHI on render.
  // Missing noteDesign, or no matching section, means find() returns
  // undefined; sanitizeStandardSectionHtml's permissive fallback for an
  // undefined section is the pre-fix behavior, so notes without noteDesign
  // metadata are unaffected.
  // noteDesign.sections can be a truthy non-array in the wild (see the
  // Array.isArray guard + reportMalformedSections in RenderedNote.js).
  const getDesignSection = (variable) => {
    const sections = chat?.contextVariables?.["local:noteDesign"]?.sections;
    return (Array.isArray(sections) ? sections : []).find((s) => s.variable === variable);
  };

  useEffect(() => {
    restoreScroll();
    // When the caller hands us a scoped list (e.g. the voice picker passes
    // just the codes the provider confirmed), show only those codes but leave
    // them unchecked — provider explicitly taps the ones they want to
    // generate addendums for.
    if (Array.isArray(initialIcd10) && initialIcd10.length > 0) {
      setIcd10(initialIcd10.map((c) => ({ label: c.code + " - " + c.description })));
    } else {
      setIcd10(
        normalizList(chat.contextVariables["local:billableCodesICD10"]).map((c) => {
          return { label: c.code + " - " + c.description };
        })
      );
    }
    // eslint-disable-next-line
  }, []);

  const generateDocs = async () => {
    setLoading(true);
    setGenerateFailed(false);
    try {
      const opt = await api.optimizeNote(
        chat.contextVariables["local:hpi"],
        chat.contextVariables["local:planOfCare"],
        icd10.filter((i) => i.checked).map((i) => i.label)
      );
      // The process can answer 200 with no usable output (e.g. its backup
      // model fails silently), so an empty parse is a failure, not "no ideas".
      const docs = parseSupportingDocs(opt?.data?.output?.supportingDoc);
      if (docs.length > 0) {
        setOptimizations(docs);
      } else {
        setGenerateFailed(true);
      }
    } catch (err) {
      console.error("ICD-10 addendum generation failed", err);
      setGenerateFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const toggleIcd = (index, val) => {
    icd10[index].checked = val;
    setIcd10([...icd10]);
  };

  const toggle = (type, codeIndex, index, val) => {
    optimizations[codeIndex][type][index].checked = val;
    setOptimizations([...optimizations]);
  };

  const allChecked = (codeIndex, type) => {
    const list = optimizations?.[codeIndex]?.[type];
    return Array.isArray(list) && list.length > 0 && list.every((item) => item.checked);
  };

  const toggleAll = (codeIndex, type) => {
    if (!optimizations?.[codeIndex]?.[type]) return;
    const target = !allChecked(codeIndex, type);
    optimizations[codeIndex][type].forEach((item) => {
      item.checked = target;
    });
    setOptimizations([...optimizations]);
  };

  const review = async () => {
    setReviewing(true);

    // Build ICD-10 label lookup: code → full description
    const icdLabelMap = {};
    for (const entry of icd10) {
      const dash = entry.label?.indexOf(" - ");
      if (dash >= 0) {
        const code = entry.label.substring(0, dash).trim();
        const desc = entry.label.substring(dash + 3).trim();
        icdLabelMap[code] = desc;
      }
    }

    // Collect checked addendums with full diagnosis descriptions
    let hpiAddendum = "";
    const pocAddendums = [];
    for (let o of optimizations) {
      for (let h of o.hpi) {
        if (h.checked) {
          hpiAddendum += (hpiAddendum ? " " : "") + h.desc;
        }
      }
      for (let p of o.planOfCare) {
        if (p.checked) {
          const diagDesc = icdLabelMap[o.code] || o.code || "";
          pocAddendums.push({ diagDesc, desc: p.desc });
        }
      }
    }

    setHpi(hpiAddendum);

    // Merge addendums into existing Plan of Care structure (preserves HTML formatting)
    const originalPoc = chat.contextVariables["local:planOfCare"];
    const pocArr = planOfCareToArray(originalPoc);

    let newPlan;
    if (pocArr && pocArr.length > 0 && pocAddendums.length > 0) {
      const merged = pocArr.map((item) => ({
        key: item.key,
        value: typeof item.value === "string" ? item.value : sectionToHTML(item.value),
      }));

      const normalize = (w) => w.toLowerCase().replace(/s$/, "");
      const prefixMatch = (a, b) => a.startsWith(b) || b.startsWith(a);

      for (const addendum of pocAddendums) {
        let bestIdx = -1;
        let bestScore = 0;
        const icdWords = addendum.diagDesc
          .toLowerCase()
          .split(/[\s,;()/]+/)
          .filter((w) => w.length > 2)
          .map(normalize);

        for (let i = 0; i < merged.length; i++) {
          const keyClean = merged[i].key
            .replace(/[-:]/g, " ")
            .replace(/\b(acute|chronic|uncontrolled|controlled|stable|poorly|on|exacerbation|screening|preventive|type)\b/gi, "")
            .trim();
          const keyWords = keyClean
            .toLowerCase()
            .split(/\s+/)
            .filter((w) => w.length > 2)
            .map(normalize);
          if (keyWords.length === 0) continue;

          let hits = 0;
          for (const kw of keyWords) {
            if (icdWords.some((iw) => prefixMatch(kw, iw))) hits++;
          }
          const score = hits / keyWords.length;
          if (score > bestScore) {
            bestScore = score;
            bestIdx = i;
          }
        }

        if (bestIdx >= 0 && bestScore > 0.3) {
          const val = merged[bestIdx].value;
          const lastUlClose = val.lastIndexOf("</ul>");
          if (lastUlClose >= 0) {
            merged[bestIdx].value =
              val.substring(0, lastUlClose) +
              '<li><span class="added">' +
              addendum.desc +
              "</span></li>" +
              val.substring(lastUlClose);
          } else {
            merged[bestIdx].value = val + '<ul><li><span class="added">' + addendum.desc + "</span></li></ul>";
          }
        } else {
          merged.push({
            key: addendum.diagDesc || "Additional Documentation",
            value: '<ul><li><span class="added">' + addendum.desc + "</span></li></ul>",
          });
        }
      }
      newPlan = merged;
    } else if (pocAddendums.length > 0) {
      let planText = pocAddendums.map((p) => p.desc).join(" ");
      let pocForApi = typeof originalPoc === "string" ? originalPoc : sectionToHTML(originalPoc);
      let backendReview = await api.optimizeNoteUpdateDocs(pocForApi, planText);
      newPlan = backendReview.data.output.update?.planOfCare || originalPoc;
    } else {
      newPlan = originalPoc;
    }
    setPlan(newPlan);

    // Build HPI diff
    let old = convertHTMLToText(chat.contextVariables["local:hpi"]);
    let newVal = convertHTMLToText(chat.contextVariables["local:hpi"] + (hpiAddendum ? " " + hpiAddendum : ""));
    let diff = diffChars(old, newVal);
    let fragment = document.createElement("div");
    diff.forEach((part) => {
      let span = document.createElement("span");
      span.classList.add(part.added ? "added" : part.removed ? "removed" : "default");
      span.appendChild(document.createTextNode(part.value));
      fragment.appendChild(span);
    });
    setHpiHTML(fragment.innerHTML.replaceAll("\n", "<br/>"));

    // Build Plan of Care preview — structured HTML with formatting
    if (Array.isArray(newPlan) && newPlan.length > 0) {
      const originalKeys = new Set((pocArr || []).map((item) => item.key));
      let html = "";
      for (const item of newPlan) {
        const val = typeof item.value === "string" ? item.value : sectionToHTML(item.value);
        const isNew = !originalKeys.has(item.key);
        html += "<strong>" + (isNew ? '<span class="added">' : "") + item.key + (isNew ? "</span>" : "") + "</strong>";
        html += val;
      }
      setPlanHTML(html);
    } else {
      let planTextOld = convertHTMLToText(typeof originalPoc === "string" ? originalPoc : sectionToHTML(originalPoc));
      let planTextNew = convertHTMLToText(typeof newPlan === "string" ? newPlan : sectionToHTML(newPlan));
      diff = diffChars(planTextOld, planTextNew);
      fragment = document.createElement("div");
      diff.forEach((part) => {
        let span = document.createElement("span");
        span.classList.add(part.added ? "added" : part.removed ? "removed" : "default");
        span.appendChild(document.createTextNode(part.value));
        fragment.appendChild(span);
      });
      setPlanHTML(fragment.innerHTML.replaceAll("\n", "<br/>"));
    }

    setReviewing(false);
  };

  const save = async () => {
    setLoadingUpdate(true);

    // Strip preview-only <span class="added"> wrappers before saving
    let cleanPlan = plan;
    if (Array.isArray(plan)) {
      cleanPlan = plan.map((item) => ({
        key: item.key,
        value: typeof item.value === "string" ? item.value.replace(/<span class="added">(.*?)<\/span>/g, "$1") : item.value,
      }));
    }

    let chatUpdate = {
      chatId: chat.id,
      chatLastUpdate: new Date(),
      contextVariables: {
        "local:planOfCare": cleanPlan,
        "local:hpi": chat.contextVariables["local:hpi"] + " " + hpi,
      },
    };
    await updateEncounter(chatUpdate, false);

    setLoadingUpdate(false);
    close();
  };

  const selectedCount = icd10.filter((i) => i.checked).length;

  return (
    <Modal
      show={true}
      close={close}
      size="x-large"
      title={t("icd10OptimizerNote.title")}
      className={"icd10-optimizer-modal dx-optnote"}
    >
      {!loading && !optimizations && (
        <>
          <div className="dx-optnote-meta">{t("icd10OptimizerNote.selectICD10Description")}</div>

          {generateFailed && (
            <div className="dx-optnote-error" role="alert">
              <i className="notranslate material-icons-outlined" aria-hidden="true">
                error_outline
              </i>
              <span>{t("icd10OptimizerNote.noSuggestions")}</span>
            </div>
          )}

          <div className="dx-optnote-list">
            {icd10.map((row, k) => {
              const parts = (row.label || "").split(" - ");
              const code = parts[0];
              const desc = (parts.slice(1).join(" - ") || row.label || "").replace(/\s*\[V\d+\]\s*$/i, "").trim();
              const checked = !!row.checked;
              return (
                <div
                  key={k}
                  className={"dx-optnote-row" + (checked ? " is-confirmed" : " is-suggested")}
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleIcd(k, !checked)}
                  onKeyDown={(e) => {
                    if (e.key === " " || e.key === "Enter") {
                      e.preventDefault();
                      toggleIcd(k, !checked);
                    }
                  }}
                >
                  <span className="dx-optnote-dot" aria-hidden="true" />
                  <span className="dx-optnote-pill">{code}</span>
                  <span className="dx-optnote-desc">{desc}</span>
                  <span className="dx-optnote-status" aria-hidden="true">
                    {checked && <i className="notranslate material-icons">check</i>}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="dx-optnote-footer">
            <button
              type="button"
              className="dx-primary"
              disabled={selectedCount === 0}
              onClick={(e) => {
                createRipple(e);
                generateDocs();
              }}
            >
              {t("icd10OptimizerNote.createAddendums")}
              {selectedCount > 0 && " (" + selectedCount + ")"}
            </button>
          </div>
        </>
      )}

      {loading && (
        <div className="vertical-align flex-center">
          <div className="meta-center flex-center">
            <div className="max-width">
              <p>{t("icd10OptimizerNote.generatingNote")}</p>

              {icd10
                .filter((i) => i.checked)
                .map((i) => (
                  <p key={i.label}>
                    <b>{i.label}</b>
                  </p>
                ))}
              <div className="mt-30 mb-30">
                <Loader className="mt-30"></Loader>
              </div>
            </div>
          </div>
        </div>
      )}

      {!loading && optimizations && plan === undefined && hpi === undefined && (
        <>
          <div className="dx-optnote-banner">
            <i className="notranslate material-icons-outlined dx-optnote-banner-icon">lightbulb</i>
            <div>
              <div>{t("icd10OptimizerNote.selectItems")}</div>
              <div className="dx-optnote-banner-sub">{t("icd10OptimizerNote.clinicalGuidelines")}</div>
            </div>
          </div>

          <div className="dx-optnote-groups">
            {optimizations.map((opt, c) => {
              const sections = [
                { type: "hpi", label: t("icd10OptimizerNote.hpi"), items: opt?.hpi || [] },
                { type: "planOfCare", label: t("icd10OptimizerNote.planOfCare"), items: opt?.planOfCare || [] },
              ];
              const codeDesc = (icd10.find((i) => i.label?.startsWith(opt.code + " "))?.label || "")
                .split(" - ")
                .slice(1)
                .join(" - ");
              return (
                <section key={c} className="dx-optnote-group">
                  <header className="dx-optnote-group-header">
                    <span className="dx-optnote-pill">{opt.code}</span>
                    {codeDesc && <span className="dx-optnote-group-desc">{codeDesc}</span>}
                  </header>

                  {sections.map(({ type, label, items }) =>
                    items.length === 0 ? null : (
                      <div key={type} className="dx-optnote-section">
                        <div className="dx-optnote-section-header">
                          <span className="dx-optnote-section-label">{label}</span>
                          <button
                            type="button"
                            className="dx-optnote-select-all"
                            onClick={() => toggleAll(c, type)}
                            aria-pressed={allChecked(c, type)}
                          >
                            {allChecked(c, type) ? t("icd10OptimizerNote.deselectAll") : t("icd10OptimizerNote.selectAll")}
                          </button>
                        </div>

                        <div className="dx-optnote-toggles">
                          {items.map((item, k) => (
                            <label key={k} className={"dx-optnote-toggle" + (item.checked ? " is-on" : "")}>
                              <Switch
                                checked={!!item.checked}
                                onChange={(val) => toggle(type, c, k, val)}
                                uncheckedIcon={false}
                                checkedIcon={false}
                                height={22}
                                width={42}
                                onColor="#4358a3"
                                offColor="#cbd2e0"
                                handleDiameter={18}
                                activeBoxShadow="0 0 0 3px rgba(67, 88, 163, 0.18)"
                              />
                              <span className="dx-optnote-toggle-text">{item.desc}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )
                  )}
                </section>
              );
            })}
          </div>

          <div className="dx-optnote-footer">
            <button
              type="button"
              className="dx-primary"
              onClick={(e) => {
                createRipple(e);
                review();
              }}
              disabled={reviewing}
            >
              {reviewing ? (
                <>
                  <Loader className="mr-10" color="white" /> {t("icd10OptimizerNote.reviewing")}
                </>
              ) : (
                t("icd10OptimizerNote.reviewAddendums")
              )}
            </button>
          </div>
        </>
      )}

      {!reviewing && (plan !== undefined || hpi !== undefined) && (
        <>
          <h4>{t("icd10OptimizerNote.newHPI")}</h4>

          <div
            className="new-note comparator-diff"
            dangerouslySetInnerHTML={{
              __html: sanitizeStandardSectionHtml(getDesignSection("hpi"), hpiHTML),
            }}
          ></div>

          <h4>{t("icd10OptimizerNote.newPlanOfCare")}</h4>
          <div
            className="new-note comparator-diff"
            dangerouslySetInnerHTML={{
              __html: sanitizeStandardSectionHtml(getDesignSection("planOfCare"), planHTML),
            }}
          ></div>

          <div className="modal-actions">
            <button className="btn btn-primary" onClick={save} disabled={loadingUpdate}>
              {loadingUpdate ? (
                <>
                  <Loader className="mr-10" color="white" /> {t("icd10OptimizerNote.reviewing")}
                </>
              ) : (
                t("icd10OptimizerNote.save")
              )}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

export default ModalIC10OptimizerNote;
